/* eslint-disable @typescript-eslint/no-explicit-any */
import {
 Box,
 Button,
 Checkbox,
 Dialog,
 DialogActions,
 DialogContent,
 DialogTitle,
 FormControlLabel,
 FormGroup,
 IconButton,
 Table,
 TableBody,
 TableCell,
 TableHead,
 TableRow,
 TextField,
 Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
 Control,
 Controller,
 UseFormRegister,
 UseFormSetValue,
 UseFormWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { displayToast } from '../../redux/toast/toastSlice';
import SelectApi from '../common/SelectApi';

type FieldKind = 'number' | 'text' | 'vehicle' | 'contract';

interface AdditionalFieldDef {
 key: string;
 kind: FieldKind;
}

const ADDITIONAL_FIELDS: AdditionalFieldDef[] = [
 { key: 'netoDoc', kind: 'number' },
 { key: 'brutoDoc', kind: 'number' },
 { key: 'tareDoc', kind: 'number' },
 { key: 'minNeto', kind: 'number' },
 { key: 'maxNeto', kind: 'number' },
 { key: 'minBruto', kind: 'number' },
 { key: 'maxBruto', kind: 'number' },
 { key: 'add1', kind: 'text' },
 { key: 'add2', kind: 'text' },
 { key: 'add3', kind: 'text' },
 { key: 'add4', kind: 'text' },
 { key: 'add5', kind: 'text' },
 { key: 'add6', kind: 'text' },
 { key: 'add7', kind: 'text' },
 { key: 'add8', kind: 'text' },
 { key: 'contractDetail', kind: 'contract' },
 { key: 'trailer1', kind: 'vehicle' },
 { key: 'trailer2', kind: 'vehicle' },
];
const ADDED_FIELDS_STORAGE = 'scales_addedFields';

export interface WeighingProductRow {
 id: number;
 productId: number;
 name: string;
 quantity: number;
}

export interface WeighingFormValues {
 car?: { Id: number; Number: string } | null;
 sender?: { Id: number; Name: string } | null;
 carrier?: { Id: number; Name: string } | null;
 receiver?: { Id: number; Name: string } | null;
 driver?: { Id: number; FullName: string } | null;
 product?: { Id: number; Name: string } | null;
 quantity?: number | string;
 fromAddress?: string;
 toAddress?: string;
 ivaz?: boolean;
 [key: string]: any;
}

interface WeighingFormProps {
 control: Control<WeighingFormValues>;
 setValue: UseFormSetValue<WeighingFormValues>;
 watch: UseFormWatch<WeighingFormValues>;
 register: UseFormRegister<WeighingFormValues>;
 products: WeighingProductRow[];
 setProducts: React.Dispatch<React.SetStateAction<WeighingProductRow[]>>;
}

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
 <Typography
  sx={{ fontSize: 14, fontWeight: 500, color: (th) => th.palette.kemek.darkGray }}
 >
  {children}
 </Typography>
);

const WeighingForm = ({
 control,
 setValue,
 watch,
 register,
 products,
 setProducts,
}: WeighingFormProps) => {
 const { t } = useTranslation();
 const dispatch = useDispatch();
 const [added, setAdded] = useState<Record<string, boolean>>({});
 const [fieldsOpen, setFieldsOpen] = useState(false);
 const [draft, setDraft] = useState<Record<string, boolean>>({});

 useEffect(() => {
  try {
   const raw = localStorage.getItem(ADDED_FIELDS_STORAGE);
   if (raw) setAdded(JSON.parse(raw));
  } catch {
   /* noop */
  }
 }, []);

 // Auto-reveal any additional field that gets a value (e.g. car-select prefill).
 const watchedValues = watch();
 useEffect(() => {
  setAdded((prev) => {
   const next = { ...prev };
   let changed = false;
   ADDITIONAL_FIELDS.forEach((f) => {
    const v = watchedValues[f.key];
    const has =
     f.kind === 'vehicle' || f.kind === 'contract'
      ? !!v?.Id
      : v !== undefined && v !== null && v !== '';
    if (has && !next[f.key]) {
     next[f.key] = true;
     changed = true;
    }
   });
   return changed ? next : prev;
  });
 }, [watchedValues]);

 const openFields = () => {
  setDraft({ ...added });
  setFieldsOpen(true);
 };
 const applyFields = () => {
  setAdded(draft);
  try {
   localStorage.setItem(ADDED_FIELDS_STORAGE, JSON.stringify(draft));
  } catch {
   /* noop */
  }
  setFieldsOpen(false);
 };

 const customerFilter = JSON.stringify({ fields: 'Id,Name' });
 const driverFilter = JSON.stringify({ fields: 'Id,FullName' });
 const carFilter = JSON.stringify({ fields: 'Id,Number' });
 const productFilter = JSON.stringify({ fields: 'Id,Name' });
 const contractFilter = JSON.stringify({ fields: 'Id,Number' });

 const totalQty = products.reduce((a, p) => a + Number(p.quantity || 0), 0);

 const addProduct = () => {
  const product = watch('product');
  const qty = Number(watch('quantity') || 0);
  if (!product?.Id) {
   dispatch(displayToast({ message: t('weighing.products.errProduct'), type: 'warning' }));
   return;
  }
  if (qty <= 0 || qty > 100) {
   dispatch(displayToast({ message: t('weighing.products.errQty'), type: 'warning' }));
   return;
  }
  if (totalQty + qty > 100) {
   dispatch(
    displayToast({
     message: t('weighing.products.errTotal', { remaining: 100 - totalQty }),
     type: 'warning',
    }),
   );
   return;
  }
  setProducts((prev) => [
   ...prev,
   { id: Date.now(), productId: product.Id, name: product.Name, quantity: qty },
  ]);
  setValue('product', null);
  setValue('quantity', '');
 };

 const removeProduct = (id: number) =>
  setProducts((prev) => prev.filter((p) => p.id !== id));

 // MUI keeps the label centered unless it knows the field is filled; values set
 // programmatically (car prefill) don't trigger that, so force shrink when filled.
 const hasVal = (v: any) => v !== undefined && v !== null && v !== '';
 const shrinkProps = (key: string) =>
  hasVal(watchedValues[key]) ? { shrink: true } : undefined;

 const renderAdditionalField = (def: AdditionalFieldDef) => {
  const label = t(`weighing.addFields.labels.${def.key}`);
  if (def.kind === 'vehicle') {
   return (
    <SelectApi<WeighingFormValues>
     key={def.key}
     name={def.key}
     url="/api/Vehicles/v2/"
     filter={carFilter}
     label={label}
     isEditable
     operator="contains"
     control={control}
     setValue={setValue}
    />
   );
  }
  if (def.kind === 'contract') {
   return (
    <SelectApi<WeighingFormValues>
     key={def.key}
     name={def.key}
     url="/api/Contracts/v1/"
     filter={contractFilter}
     label={label}
     isEditable
     operator="contains"
     control={control}
     setValue={setValue}
    />
   );
  }
  return (
   <TextField
    key={def.key}
    {...register(def.key)}
    size="small"
    type={def.kind === 'number' ? 'number' : 'text'}
    label={label}
    InputLabelProps={shrinkProps(def.key)}
   />
  );
 };

 return (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
   {/* Products */}
   <Box
    sx={{
     display: 'flex',
     flexDirection: 'column',
     gap: 1,
     p: 1.5,
     borderRadius: 1,
     backgroundColor: (th) => th.palette.kemek.lightBlue,
    }}
   >
    <SectionTitle>{t('weighing.products.title')}</SectionTitle>
    <SelectApi<WeighingFormValues>
     name="product"
     url="/api/Products/v2/"
     filter={productFilter}
     label={t('weighing.products.product')}
     isEditable
     operator="contains"
     control={control}
     setValue={setValue}
    />
    <Controller
     name="quantity"
     control={control}
     render={({ field }) => (
      <TextField
       {...field}
       type="number"
       size="small"
       label={t('weighing.products.quantity')}
       inputProps={{ min: 0, max: 100 }}
       sx={{ backgroundColor: (th) => th.palette.background.paper }}
      />
     )}
    />
    <Box sx={{ display: 'flex', gap: 1 }}>
     <Button size="small" onClick={addProduct}>
      {t('weighing.products.add')}
     </Button>
     <Button
      size="small"
      color="secondary"
      onClick={() => {
       setValue('product', null);
       setValue('quantity', '');
      }}
     >
      {t('weighing.products.clear')}
     </Button>
    </Box>
    {products.length > 0 && (
     <Table size="small">
      <TableHead>
       <TableRow>
        <TableCell>{t('weighing.products.colName')}</TableCell>
        <TableCell align="right">{t('weighing.products.colQty')}</TableCell>
        <TableCell />
       </TableRow>
      </TableHead>
      <TableBody>
       {products.map((p) => (
        <TableRow key={p.id}>
         <TableCell>{p.name}</TableCell>
         <TableCell align="right">{p.quantity}</TableCell>
         <TableCell align="right">
          <IconButton
           size="small"
           sx={{ color: (th) => th.palette.kemek.red }}
           onClick={() => removeProduct(p.id)}
          >
           ✕
          </IconButton>
         </TableCell>
        </TableRow>
       ))}
      </TableBody>
     </Table>
    )}
   </Box>

   {/* Data */}
   <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
     <SectionTitle>{t('weighing.fields.dataTitle')}</SectionTitle>
     <Button size="small" onClick={openFields}>
      {t('weighing.addFields.button')}
     </Button>
    </Box>
    <SelectApi<WeighingFormValues>
     name="sender"
     url="/api/Customers/v2/"
     filter={customerFilter}
     label={t('weighing.fields.sender')}
     isEditable
     operator="contains"
     control={control}
     setValue={setValue}
    />
    <SelectApi<WeighingFormValues>
     name="carrier"
     url="/api/Customers/v2/"
     filter={customerFilter}
     label={t('weighing.fields.carrier')}
     isEditable
     operator="contains"
     control={control}
     setValue={setValue}
    />
    <SelectApi<WeighingFormValues>
     name="receiver"
     url="/api/Customers/v2/"
     filter={customerFilter}
     label={t('weighing.fields.receiver')}
     isEditable
     operator="contains"
     control={control}
     setValue={setValue}
    />
    <SelectApi<WeighingFormValues>
     name="driver"
     url="/api/Drivers/v2/"
     filter={driverFilter}
     label={t('weighing.fields.driver')}
     isEditable
     operator="contains"
     control={control}
     setValue={setValue}
    />
    <TextField
     {...register('fromAddress')}
     size="small"
     label={t('weighing.fields.fromAddress')}
     InputLabelProps={shrinkProps('fromAddress')}
    />
    <TextField
     {...register('toAddress')}
     size="small"
     label={t('weighing.fields.toAddress')}
     InputLabelProps={shrinkProps('toAddress')}
    />
    {ADDITIONAL_FIELDS.filter((f) => added[f.key]).map(renderAdditionalField)}
   </Box>

   {/* Additional fields picker */}
   <Dialog open={fieldsOpen} onClose={() => setFieldsOpen(false)} fullWidth>
    <DialogTitle>{t('weighing.addFields.title')}</DialogTitle>
    <DialogContent>
     <FormGroup>
      {ADDITIONAL_FIELDS.map((f) => (
       <FormControlLabel
        key={f.key}
        control={
         <Checkbox
          checked={!!draft[f.key]}
          onChange={(e) =>
           setDraft((p) => ({ ...p, [f.key]: e.target.checked }))
          }
         />
        }
        label={t(`weighing.addFields.labels.${f.key}`)}
       />
      ))}
     </FormGroup>
    </DialogContent>
    <DialogActions>
     <Button color="secondary" onClick={() => setFieldsOpen(false)}>
      {t('weighing.addFields.cancel')}
     </Button>
     <Button variant="contained" onClick={applyFields}>
      {t('weighing.addFields.apply')}
     </Button>
    </DialogActions>
   </Dialog>
  </Box>
 );
};

export default WeighingForm;
